import { useMemo } from 'react';
import { Box, Container, Group } from '@mantine/core';
import { IconArrowLeft } from '@tabler/icons-react';
import { useNavigate, useParams } from 'react-router-dom';
import { PageHeader } from '../common/PageHeader';
import { ActionButton } from '../common/ActionButton';
import { FinancialsPage } from '../financials/FinancialsPage';
import { useSyncSelectedGroupFromSubawardee } from '../../hooks/useSyncSelectedGroupFromSubawardee';
import { useGroup } from '../../contexts/GroupContext';
import {
  buildSubawardeeLabelLookup,
  getSubawardeeDisplayLabel,
} from '../../utils/adminSubawardeeUtils';

/**
 * Admin detail view for one subawardee's financials (same UI as the subawardee page).
 */
export function AdminFinancialsDetailPage() {
  const { subawardee: subawardeeParam } = useParams<{ subawardee: string }>();
  const subawardee = subawardeeParam ? decodeURIComponent(subawardeeParam) : null;
  const navigate = useNavigate();
  const { availableGroups } = useGroup();
  useSyncSelectedGroupFromSubawardee(subawardee);

  const title = useMemo(() => {
    if (!subawardee) return 'Financials';
    const labelById = buildSubawardeeLabelLookup(availableGroups);
    return getSubawardeeDisplayLabel(subawardee, labelById) ?? subawardee;
  }, [subawardee, availableGroups]);

  return (
    <Container size="xl" mt="xl" pb={80} sx={{ minHeight: 'calc(100vh - 140px)' }} px={0}>
      <Box px="sm" mb="xs">
        <Group spacing="sm" mb="xs">
          <ActionButton
            onClick={() => navigate('/admin/financials')}
            label="Back to Financials"
            ariaLabel="Back to financials summary"
            actionType="secondary"
            leftIcon={<IconArrowLeft size={16} />}
            sx={{
              height: 32,
              fontSize: '14px',
              border: 'none',
              '&:hover': {
                border: 'none',
              },
            }}
          />
        </Group>
      </Box>

      <PageHeader
        title={title}
        subtitle="Track cumulative federal and match spend against award targets, and report monthly to-date totals."
      />

      {subawardee ? <FinancialsPage groupScope={subawardee} embedded /> : null}
    </Container>
  );
}
